import { useEffect, useMemo, useState } from "react";
import type { PrecipNowcast } from "@yala/shared";
import { windDirLabel, uvBand, pulseColor, aqiBand, rainBadge, hmFromIso, timeInTz } from "../lib/worldStrip";
import { useT } from "../hooks/useLang";

interface Props {
  compact?: boolean;
  hostAqi: number | null;
  hostPm25: number | null;
  // Weather for the Yala host block — fetched in useWorldWeather.
  hostWeather: {
    tempC: number | null;
    apparentTempC: number | null;
    humidity: number | null;
    rainNow: number | null;
    windKmh: number | null;
    windDeg: number | null;
    uv: number | null;
    cloudPct: number | null;
    pressurehPa: number | null;
    visKm: number | null;
    isDay: boolean | null;
    condition: string;
    sunrise: string | null;
    sunset: string | null;
    daily: Array<{ date: string; tempMaxC: number; tempMinC: number; precipMm: number; precipProb: number }>;
  } | null;
  hostPulse: {
    iticEvents: number;
    openReports: number;
    news24h: number;
    shuttleLive: number;
  };
  precipNowcast: PrecipNowcast | null;
}


const fmtTemp = (t: number | null) => (t == null ? "—" : `${Math.round(t)}°`);
const fmtPct = (p: number | null) => (p == null ? "—" : `${Math.round(p)}%`);
const fmtInt = (n: number | null) => (n == null ? "—" : String(Math.round(n)));
const fmtFix = (n: number | null, d = 1) => (n == null ? "—" : n.toFixed(d));

function dayLabel(iso: string, hostTz = "Asia/Bangkok"): string {
  const d = new Date(iso + "T00:00:00");
  return new Intl.DateTimeFormat("en-US", { timeZone: hostTz, weekday: "short" }).format(d).toUpperCase();
}

export function WorldStrip({ compact = false, hostAqi, hostPm25, hostWeather, hostPulse, precipNowcast }: Props) {
  const t = useT();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const aqi = aqiBand(hostAqi);
  const uv = uvBand(hostWeather?.uv ?? null);
  const rain = rainBadge(precipNowcast);
  const wind = hostWeather?.windKmh ?? null;
  const windDir = windDirLabel(hostWeather?.windDeg ?? null);
  const sunrise = hmFromIso(hostWeather?.sunrise ?? null);
  const sunset = hmFromIso(hostWeather?.sunset ?? null);
  const sunLabel = hostWeather?.isDay === false ? "SUNRISE" : "SUNSET";
  const sunTime = hostWeather?.isDay === false ? sunrise : sunset;
  const hostTime = timeInTz("Asia/Bangkok", now);
  const forecastDays = (hostWeather?.daily ?? []).slice(0, 5);

  const readout = (
    <div className="weather-readout">
      <section className="world-host">
        <div className="world-host-head">
          <span className="eyebrow mono">Yala · host</span>
          <span className="mono caption">
            {hostTime.hms} · {hostTime.offset}
          </span>
        </div>
        <div className="world-host-row">
          <div className="world-host-now">
            <span className="world-host-temp">{fmtTemp(hostWeather?.tempC ?? null)}</span>
            <span className="world-host-cond">{hostWeather?.condition ?? "—"}</span>
            <span className="world-host-feels mono">
              FL {fmtTemp(hostWeather?.apparentTempC ?? null)}
            </span>
          </div>
          <div className="world-host-stats">
            <div className="world-stat">
              <span className="lbl">{t("HUMIDITY", "ความชื้น")}</span>
              <span className="val mono">{fmtPct(hostWeather?.humidity ?? null)}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">{t("WIND", "ลม")}</span>
              <span className="val mono">{wind != null ? `${Math.round(wind)}` : "—"}</span>
              <span className="sub mono">{wind != null ? `KM/H ${windDir}` : "—"}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">UV</span>
              <span className="val mono" style={{ color: uv.color }}>{fmtFix(hostWeather?.uv ?? null, 1)}</span>
              <span className="sub mono" style={{ color: uv.color }}>{uv.label}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">AQI</span>
              <span className="val mono" style={{ color: aqi.color }}>{hostAqi ?? "—"}</span>
              <span className="sub mono" style={{ color: aqi.color }}>
                {hostPm25 != null ? `PM2.5 ${hostPm25.toFixed(1)}` : aqi.label}
              </span>
            </div>
            <div className="world-stat">
              <span className="lbl">NOWCAST</span>
              <span className="val mono" style={{ color: rain.color }}>{rain.label}</span>
              <span className="sub mono" style={{ color: rain.color }}>{rain.sub}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">RAIN NOW</span>
              <span className="val mono">{fmtFix(hostWeather?.rainNow ?? null, 1)}</span>
              <span className="sub mono">MM/H · CLOUD {fmtPct(hostWeather?.cloudPct ?? null)}</span>
            </div>
            <div className="world-stat">
              <span className="lbl">VIS</span>
              <span className="val mono">{fmtFix(hostWeather?.visKm ?? null, 1)}</span>
              <span className="sub mono">
                KM · {fmtInt(hostWeather?.pressurehPa ?? null)} hPa
              </span>
            </div>
            <div className="world-stat">
              <span className="lbl">{sunLabel}</span>
              <span className="val mono">{sunTime}</span>
              <span className="sub mono">↑ {sunrise} · ↓ {sunset}</span>
            </div>
          </div>
          <div className="world-host-pulse" aria-label="Yala live operational pulse">
            <div className="pulse-head">
              <span className="dot live" />
              <span className="eyebrow mono">Yala pulse</span>
            </div>
            <div className="pulse-grid">
              <div className="pulse-cell">
                <span className="lbl">iTIC EVT</span>
                <span
                  className="val mono"
                  style={{ color: pulseColor(hostPulse.iticEvents, 5, 15) }}
                >
                  {hostPulse.iticEvents}
                </span>
              </div>
              <div className="pulse-cell">
                <span className="lbl">CR OPEN</span>
                <span
                  className="val mono"
                  style={{ color: pulseColor(hostPulse.openReports, 6, 21) }}
                >
                  {hostPulse.openReports}
                </span>
              </div>
              <div className="pulse-cell">
                <span className="lbl">NEWS 24H</span>
                <span className="val mono">{hostPulse.news24h}</span>
              </div>
              <div className="pulse-cell">
                <span className="lbl">MUNI BUS</span>
                <span className="val mono">{hostPulse.shuttleLive}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The 5-day rain outlook used to sit at the end of the host row, where
          it wrapped onto a second line that the 72px strip clipped away
          entirely — a rendered data element nobody could ever see. For a
          flood city the rain outlook is the point of the band, so it gets
          the second track, ahead of the feed chips.

          The track is content-sized and disappears when there is no forecast:
          a reserved 280px holding nothing is exactly the space-with-no-job
          this band used to be guilty of. */}
      {forecastDays.length > 0 && (
        <div className="world-forecast-slot">
          <div className="world-forecast" aria-label="5-day rain probability">
            {forecastDays.map((d, i) => (
              <div className="world-day" key={d.date}>
                <span className="world-day-name mono">{i === 0 ? "TODAY" : dayLabel(d.date)}</span>
                <span className="world-day-bar" title={Number.isFinite(d.precipProb) ? `${d.precipProb}% rain · ${d.precipMm.toFixed(1)}mm${d.precipProb < 0 || d.precipProb > 100 ? " · outside 0–100% scale" : ""}` : "Rain probability unavailable"}>
                  <span
                    className="world-day-fill"
                    style={{
                      height: `${Number.isFinite(d.precipProb) ? Math.max(0, Math.min(100, Math.round(d.precipProb))) : 0}%`,
                      background:
                        d.precipProb >= 70 ? "var(--bad)"
                          : d.precipProb >= 40 ? "var(--warn)"
                            : "var(--data)",
                    }}
                  />
                </span>
                <span className="world-day-pct mono">{Number.isFinite(d.precipProb) ? `${Math.round(d.precipProb)}%${d.precipProb > 100 ? " ↑" : d.precipProb < 0 ? " ↓" : ""}` : "—"}</span>
                <span className="world-day-temp mono">
                  {Math.round(d.tempMinC)}–{Math.round(d.tempMaxC)}°
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feed health lives in the bottom bar, not here: this band is one
          question — what the local conditions and the rain outlook are — and
          the feed chips answer a different one. */}

    </div>
  );

  if (!compact) return <div className="world-strip">{readout}</div>;
  return (
    <div className="world-strip world-strip-compact">
      <details className="weather-disclosure">
        <summary className="weather-summary" aria-label="Weather details">
          <span className="weather-primary"><span className="eyebrow">{t("RAIN", "ฝน")}</span> <strong className="mono">{fmtFix(hostWeather?.rainNow ?? null, 1)}</strong> <span className="mono">mm/h</span></span>
          <span className="weather-primary"><span className="eyebrow">{t("NOWCAST", "เรดาร์ฝน")}</span> <strong className="mono">{rain.label}</strong></span>
          <span className="weather-primary"><span className="eyebrow">AQI</span> <strong className="mono" style={{color:aqi.color}}>{hostAqi ?? "—"}</strong></span>
          <span className="weather-more mono">{t("WEATHER ▾", "สภาพอากาศ ▾")}</span>
        </summary>
        {readout}
      </details>
    </div>
  );
}
